import { useMemo, useCallback } from 'react';
import {
  useScheduler,
  BookingBatchUpdate,
  BookingBatchDeleteResult,
  BookingBatchUpdateResult,
  SeriesFutureUpdate,
  SeriesFutureUpdateResult,
} from '@/contexts/SchedulerContext';
import { useDataMode, ScreenName } from '@/contexts/DataModeContext';
import { useCompany } from '@/contexts/CompanyContext';
import { Resource, Booking, Project, Client, CostCentre, ProjectDetails, AppSettings, SchedulerGroup } from '@/types';

type DataMode = 'demo' | 'live';

interface ScreenData {
  resources: Resource[];
  bookings: Booking[];
  projects: Project[];
  clients: Client[];
  costCentres: CostCentre[];
  projectDetails: ProjectDetails[];
  schedulerGroups: SchedulerGroup[];
  settings: AppSettings;
  isDemo: boolean;
  isLive: boolean;
  hasLiveData: boolean;
  currentMode: DataMode;
  /**
   * False until the company, its settings and its scheduler groups have
   * resolved. Until then `settings` holds defaults, so anything derived from
   * it (such as the grouping method) must not be trusted yet.
   */
  isSettingsReady: boolean;
  addBooking: (booking: Omit<Booking, 'id'>) => Promise<Booking | undefined>;
  createBatchBookings: (bookings: Omit<Booking, 'id'>[]) => Promise<void>;
  updateBooking: (id: string, booking: Partial<Booking>) => Promise<void>;
  deleteBooking: (id: string) => Promise<void>;
  /** Soft-deletes a whole selection in one request. */
  deleteBookingsBatch: (ids: Array<string | number>) => Promise<BookingBatchDeleteResult>;
  /** Applies per-booking changes to a whole selection in one request. */
  updateBookingsBatch: (updates: BookingBatchUpdate[]) => Promise<BookingBatchUpdateResult>;
  /** Applies an "all future bookings" edit to a recurring series in one request. */
  updateSeriesFuture: (recurrenceGroupId: string, update: SeriesFutureUpdate) => Promise<SeriesFutureUpdateResult>;
  updateSettings: (settings: Partial<AppSettings>) => Promise<void>;
  updateUserSettings: (settings: Partial<AppSettings>) => Promise<void>;
  addSchedulerGroup: (group: Omit<SchedulerGroup, 'id'>) => Promise<void>;
  updateSchedulerGroup: (id: string, updates: Partial<SchedulerGroup>) => Promise<void>;
  saveSchedulerGroup: (id: string) => Promise<void>;
  deleteSchedulerGroup: (id: string) => Promise<void>;
}

export function useScreenData(screen: ScreenName): ScreenData {
  const { 
    mockData, 
    liveData, 
    hasLiveData, 
    isLoading,
    addBooking: contextAddBooking, 
    createBatchBookings: contextCreateBatchBookings,
    updateBooking: contextUpdateBooking, 
    deleteBooking: contextDeleteBooking, 
    deleteBookingsBatch: contextDeleteBookingsBatch,
    updateBookingsBatch: contextUpdateBookingsBatch,
    updateSeriesFuture: contextUpdateSeriesFuture,
    updateSettings: contextUpdateSettings,
    updateUserSettings: contextUpdateUserSettings,
    addSchedulerGroup: contextAddSchedulerGroup,
    updateSchedulerGroup: contextUpdateSchedulerGroup,
    saveSchedulerGroup: contextSaveSchedulerGroup,
    deleteSchedulerGroup: contextDeleteSchedulerGroup
  } = useScheduler();
  const { getScreenMode } = useDataMode();
  const { isLoading: isCompanyLoading } = useCompany();
  
  const currentMode = getScreenMode(screen);
  const isDemo = currentMode === 'demo';
  const isLive = currentMode === 'live';

  const data = useMemo(() => {
    if (isDemo) {
      return mockData;
    }
    return liveData;
  }, [isDemo, mockData, liveData]);

  const addBooking = useCallback(async (booking: Omit<Booking, 'id'>): Promise<Booking | undefined> => {
    return await contextAddBooking(booking, currentMode);
  }, [contextAddBooking, currentMode]);

  const createBatchBookings = useCallback(async (bookings: Omit<Booking, 'id'>[]) => {
    await contextCreateBatchBookings(bookings, currentMode);
  }, [contextCreateBatchBookings, currentMode]);

  const updateBooking = useCallback(async (id: string, booking: Partial<Booking>) => {
    await contextUpdateBooking(id, booking, currentMode);
  }, [contextUpdateBooking, currentMode]);

  const deleteBooking = useCallback(async (id: string) => {
    await contextDeleteBooking(id, currentMode);
  }, [contextDeleteBooking, currentMode]);

  const deleteBookingsBatch = useCallback(
    (ids: Array<string | number>) => contextDeleteBookingsBatch(ids, currentMode),
    [contextDeleteBookingsBatch, currentMode]
  );

  const updateBookingsBatch = useCallback(
    (updates: BookingBatchUpdate[]) => contextUpdateBookingsBatch(updates, currentMode),
    [contextUpdateBookingsBatch, currentMode]
  );

  const updateSeriesFuture = useCallback(
    (recurrenceGroupId: string, update: SeriesFutureUpdate) =>
      contextUpdateSeriesFuture(recurrenceGroupId, update, currentMode),
    [contextUpdateSeriesFuture, currentMode]
  );

  const updateSettings = useCallback(async (settings: Partial<AppSettings>) => {
    await contextUpdateSettings(settings, currentMode);
  }, [contextUpdateSettings, currentMode]);

  const updateUserSettings = useCallback(async (settings: Partial<AppSettings>) => {
    await contextUpdateUserSettings(settings, currentMode);
  }, [contextUpdateUserSettings, currentMode]);

  const addSchedulerGroup = useCallback(async (group: Omit<SchedulerGroup, 'id'>) => {
    await contextAddSchedulerGroup(group, currentMode);
  }, [contextAddSchedulerGroup, currentMode]);

  const updateSchedulerGroup = useCallback(async (id: string, updates: Partial<SchedulerGroup>) => {
    await contextUpdateSchedulerGroup(id, updates, currentMode);
  }, [contextUpdateSchedulerGroup, currentMode]);

  const saveSchedulerGroup = useCallback(async (id: string) => {
    await contextSaveSchedulerGroup(id, currentMode);
  }, [contextSaveSchedulerGroup, currentMode]);

  const deleteSchedulerGroup = useCallback(async (id: string) => {
    await contextDeleteSchedulerGroup(id, currentMode);
  }, [contextDeleteSchedulerGroup, currentMode]);

  return {
    ...data,
    isDemo,
    isLive,
    hasLiveData,
    currentMode,
    isSettingsReady: !isCompanyLoading && !isLoading,
    addBooking,
    createBatchBookings,
    updateBooking,
    deleteBooking,
    deleteBookingsBatch,
    updateBookingsBatch,
    updateSeriesFuture,
    updateSettings,
    updateUserSettings,
    addSchedulerGroup,
    updateSchedulerGroup,
    saveSchedulerGroup,
    deleteSchedulerGroup,
  };
}
